import React, { useState } from 'react'
import ColumnLabel from '@/main/components/common/columnLabel/columnLabel';
import CardInfo from '@/main/components/shared/card/cardInfo/cardInfo';
import QuoteSupplierItemStatusComponent from '@/main/components/shared/status/quoteSupplierItemStatusComponent';
import RemainingTimeComponent from '@/main/components/shared/remainingTime/remainingTimeComponent';
import { StatusQuoteSupplierItem } from '@/main/services/enum/StatusQuoteSupplierItem';
import { SkeletonLoading } from '@/main/components/shared/styles';
import { IdName } from '@/main/models/common';
import { getGuidScreenByRoute, getTranslateByKey, isShowControlByElementId } from '@/main/services/utils';
import { Controls } from '@/main/services/constants/constantsControl';
import CopyToClipboardText from '@/main/components/shared/copyToClipboardText/copyToClipboardText';
import * as S from './styles';
import { IconButton } from '@mui/material';
import { Info } from '@mui/icons-material';
import ClaimDetailsSearchPendingAnswer from '../../../pendingToAnswer/components/ClaimDetailsSearchPendingAnswer/ClaimDetailsSearchPendingAnswer';
import { RoutesConstants } from '@/main/routes/routesConstants';

interface IHeaderAnswerProps {
  claimNr?: string;
  code: string;
  status: IdName;
  insurerName: string;
  bodyshopName: string;
  bodyshopContactName: string;
  bodyshopPhoneNr: string;
  bodyshopEmail: string;
  vehicle: string;
  licensePlate: string;
  vin: string;
  deliveryAddress: string;
  isLoading?: boolean;
  deadlineAnswer: string;
  isFinishedTime: boolean;
  guidQuote: string;
  handleFinishTime: () => void;
  bodyContent?: React.ReactNode;
  showRemainingTimeAllStatuses?: boolean;
}

function HeaderAnswer({
  claimNr,
  status,
  code,
  insurerName,
  bodyshopName,
  bodyshopContactName,
  bodyshopPhoneNr,
  bodyshopEmail,
  vehicle,
  licensePlate,
  vin,
  deliveryAddress,
  isLoading = false,
  deadlineAnswer,
  isFinishedTime,
  guidQuote,
  handleFinishTime,
  bodyContent,
  showRemainingTimeAllStatuses = false,
}: IHeaderAnswerProps) {

  const [openModalClaimDetails, setOpenModalClaimDetails] = useState<boolean>(false);
  const [selectedGuidQuote, setSelectedGuidQuote] = useState<string | null>(null);

  function renderContentLoading(
    content: string | React.ReactNode,
    widthSkeleton = '100%',
    heightSkeleton = '20px'
  ) {
    return isLoading
      ? <SkeletonLoading $width={widthSkeleton} $height={heightSkeleton} />
      : content
  }
  const fakeStatusExpired = {
    id: 999,
    name: (getTranslateByKey('information.expired'))
  }

  function handleOpenClaimDetails(guid: string) {
    setSelectedGuidQuote(guid);
    setOpenModalClaimDetails(true);
  }

  const showRemainingTime =
    !isFinishedTime &&
    !!deadlineAnswer &&
    (showRemainingTimeAllStatuses ||
      status.id === StatusQuoteSupplierItem.Pending);

  return (
    <S.Container>
      {selectedGuidQuote && (
        <ClaimDetailsSearchPendingAnswer
          guidQuote={selectedGuidQuote}
          openModalClaimDetails={openModalClaimDetails}
          setOpenModalClaimDetails={setOpenModalClaimDetails}
          guidScreen={getGuidScreenByRoute(RoutesConstants.quotationCartCreateAnswer)}
        />
      )}
      <CardInfo
        headerContent={
          <S.ContentHeaderCard>
            <S.AreaInfoHeader>
              <S.TitleCard>
                {getTranslateByKey('information.claimNr')}: {!isLoading && (claimNr || <S.EmptyClaimNumber />)}
                <IconButton
                  size="medium"
                  color="primary"
                  id="id-answerClaimDetails-icon"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenClaimDetails(guidQuote);
                  }}
                  style={{
                    marginLeft: '0.5rem',
                    alignSelf: 'center'
                  }}
                >
                  <Info />
                </IconButton>
              </S.TitleCard>
              {renderContentLoading('', '100px', '20px')}
              <S.TitleCard>
                {getTranslateByKey('information.quoteNr')}: {!isLoading && (code || <S.EmptyClaimNumber />)}
              </S.TitleCard>
              {renderContentLoading('', '100px', '20px')}
              {renderContentLoading(<QuoteSupplierItemStatusComponent status={isFinishedTime ? fakeStatusExpired : status} />, '100px', '32px')}
            </S.AreaInfoHeader>

            {showRemainingTime &&
              <S.AreaRemainingTime>
                <S.ItemSummary>
                  {getTranslateByKey('information.remainingTimeToExpire')}:
                </S.ItemSummary>
                <RemainingTimeComponent
                  showExpire
                  finishTime={deadlineAnswer}
                  onTimeFinished={handleFinishTime}
                />
              </S.AreaRemainingTime>
            }
          </S.ContentHeaderCard>
        }
        bodyContent={
          bodyContent ??
          <S.ContentBodyCard>
            <S.AreaInfoOrder>
              <S.GridItem>
                <ColumnLabel
                  label={getTranslateByKey('information.insurerName')}
                  content={<S.BoxText><S.TextInfoHeader title={insurerName}>{renderContentLoading(insurerName)}</S.TextInfoHeader></S.BoxText>}
                />
              </S.GridItem>
              <S.GridItem>
                <ColumnLabel
                  label={getTranslateByKey('information.vehicle')}
                  content={<S.BoxText><S.TextInfoHeader title={vehicle}>{renderContentLoading(vehicle)}</S.TextInfoHeader></S.BoxText>}
                />
              </S.GridItem>
              <S.GridItem>
                <ColumnLabel
                  label={getTranslateByKey('information.bodyshopName')}
                  content={<S.BoxText><S.TextInfoHeader title={bodyshopName}>{renderContentLoading(bodyshopName)}</S.TextInfoHeader></S.BoxText>}
                />
              </S.GridItem>
              <S.GridItem $span={2}>
                <ColumnLabel
                  label={getTranslateByKey('information.deliveryAddress')}
                  content={<S.TextInfoHeaderDeliveryAddress title={deliveryAddress}>{renderContentLoading(deliveryAddress)}</S.TextInfoHeaderDeliveryAddress>}
                />
              </S.GridItem>
              {isShowControlByElementId(Controls.IdLicenseAnsweredPlate) && (
                <S.GridItem>
                  <ColumnLabel
                    label={getTranslateByKey('information.licensePlate')}
                    content={<S.BoxText><S.TextInfoHeader title={licensePlate}>{renderContentLoading(licensePlate)}</S.TextInfoHeader></S.BoxText>}
                  />
                </S.GridItem>
              )}
              {isShowControlByElementId(Controls.IdVin) && (
                <S.GridItem>
                  <ColumnLabel
                    label={getTranslateByKey('information.vin')}
                    content={<S.BoxText>
                      <S.TextInfoHeader>
                        {renderContentLoading(
                          <CopyToClipboardText
                            label={getTranslateByKey('information.vin')}
                            valueToCopy={vin}
                          >
                            {vin}
                          </CopyToClipboardText>
                        )}
                      </S.TextInfoHeader>
                    </S.BoxText>}
                  />
                </S.GridItem>
              )}


              {isShowControlByElementId(Controls.IdBodyshopContactName) && (
                <S.GridItem>
                  <ColumnLabel
                    label={getTranslateByKey('information.bodyshopContact')}
                    content={<S.BoxText><S.TextInfoHeader title={bodyshopContactName}>{renderContentLoading(bodyshopContactName)}</S.TextInfoHeader></S.BoxText>}
                  />
                </S.GridItem>
              )}
              {isShowControlByElementId(Controls.IdBodyshopPhoneNr) && (
                <S.GridItem>
                  <ColumnLabel
                    label={getTranslateByKey('information.bodyshopPhone')}
                    content={<S.BoxText><S.TextInfoHeader title={bodyshopPhoneNr}>{renderContentLoading(bodyshopPhoneNr)}</S.TextInfoHeader></S.BoxText>}
                  />
                </S.GridItem>
              )}
              {isShowControlByElementId(Controls.IdBodyshopEmail) && (
                <S.GridItem>
                  <ColumnLabel
                    label={getTranslateByKey('information.bodyshopEmailAbbr')}
                    content={<S.BoxText><S.TextInfoHeader title={bodyshopEmail}>{renderContentLoading(bodyshopEmail)}</S.TextInfoHeader></S.BoxText>}
                  />
                </S.GridItem>
              )}
            </S.AreaInfoOrder>
          </S.ContentBodyCard>
        }
      />
    </S.Container>
  )
}

export default HeaderAnswer